import React, { useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight, faCircleNotch } from '@fortawesome/free-solid-svg-icons'

import { PLEDGE_MAX } from 'src/components/Validation'

import Frequency from '../Frequency'
import Amount from '../Amount'
import PageHeader from '../PageHeader'
import PartnerItemSelect from '../PartnerItemSelect'
import ProjectSelect from '../ProjectSelect'
import MatchingBanner from '../MatchingBanner'
import useValidation from '../useValidation'

// Plugin slots this step hosts (see the v7 plugin contract). Rendered inline
// below and validated together on Continue.
const SLOTS = [ 'afterIntro', 'afterAmounts', 'afterSubscription' ]

const Start = ({
  i18n, donation, plugins, currencies, frequencies,
  beneficiarySelect, partnerItems, goalPartnerItem, disableDonationSplit,
  canadianReceipt, giftAid, matching,
  processing = false,
  onContinue,
}) => {
  const validation = useValidation({
    i18n,
    currency: donation.currency,
    fieldSet: {
      amount: {
        required: true,
        min: donation.minAmount,
        max: PLEDGE_MAX,
      },
    },
  })

  // Amount changes flow through donation.setAmount (not a controlled input
  // we own) so the natural place to clear stale errors is an effect on the
  // amount value rather than wrapping an onChange.
  useEffect(() => {
    validation.resetErrorsFor('amount')
  }, [ donation.state.amount ])

  const handleSubmit = e => {
    e.preventDefault()
    // Run both validations regardless so all errors surface in one pass,
    // then bail if either failed.
    const amountValid = validation.validate(donation.state)
    const beneficiaryValid = beneficiarySelect.validate()
    const pluginsValid = plugins.validateSlots(SLOTS, { donation })
    if (!amountValid || !beneficiaryValid || !pluginsValid) return
    onContinue()
  }

  // Validation returns errors as arrays; Amount expects a single string.
  const errors = { amount: validation.errors.amount?.[0] }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      {/* Matching banner takes the subtitle slot when a match is active
          (bossman copy direction: drop static subtitles, surface the
          match in that position on Page 1). PageHeader's subtitle prop
          accepts JSX directly so the banner inherits the tight
          subtitle-rhythm gap instead of the form's space-y-4. When no
          match, subtitle is falsy and the heading stands alone. */}
      <PageHeader
        heading={i18n.t('steps.start.heading')}
        subtitle={matching && <MatchingBanner i18n={i18n} matching={matching} />}
      />

      {plugins.renderSlot('afterIntro', { donation })}

      {beneficiarySelect.showPartnerItemSelect && (
        <PartnerItemSelect
          i18n={i18n}
          partnerItem={beneficiarySelect.partnerItem}
          setPartnerItem={beneficiarySelect.setPartnerItem}
          partnerItems={partnerItems}
          goalPartnerItem={goalPartnerItem}
          disableDonationSplit={disableDonationSplit}
          error={beneficiarySelect.partnerItemError}
        />
      )}
      {beneficiarySelect.showProjectSelect && (
        <ProjectSelect
          i18n={i18n}
          projectId={beneficiarySelect.projectId}
          setProjectId={beneficiarySelect.setProjectId}
          projects={goalPartnerItem.projects}
          prompt={beneficiarySelect.projectPrompt}
          error={beneficiarySelect.projectError}
        />
      )}

      <Frequency
        i18n={i18n}
        frequencies={frequencies}
        value={donation.state.subscription}
        onChange={donation.setSubscription}
      />

      {plugins.renderSlot('afterSubscription', { donation })}

      <Amount
        i18n={i18n}
        donation={donation}
        currencies={currencies}
        errors={errors}
        multiOrg={beneficiarySelect.multiOrg}
        multiProject={beneficiarySelect.multiProject}
      />

      {plugins.renderSlot('afterAmounts', { donation })}

      {/* Region-gated opt-ins. Each hook returns { available, render,
          value }; render() resolves to null when the active beneficiary
          doesn't qualify, so wrapping the section on availability
          keeps the form rhythm tight when neither shows. Bossman can
          relocate either later — landing them here matches "before
          Continue" per current direction. */}
      {(canadianReceipt.available || giftAid.available) && (
        <div className="space-y-2">
          {canadianReceipt.render()}
          {giftAid.render()}
        </div>
      )}

      <button
        type="submit"
        disabled={processing}
        className="btn btn-primary flex w-full items-center justify-center gap-2"
      >
        {i18n.t('continue')}
        <FontAwesomeIcon icon={processing ? faCircleNotch : faArrowRight} spin={processing} className="rtl:-scale-x-100" />
      </button>
    </form>
  )
}

export default Start
